import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import CTAButton from "../components/CTAButton";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { SITE, FIND_CARE_SECTIONS } from "../lib/site";

function SituationSection({ section, index }) {
  return (
    <Reveal>
      <article
        id={section.id}
        data-testid={`find-care-section-${section.id}`}
        className="scroll-mt-28 border-t border-inkline py-12 first:border-t-0 md:py-16"
      >
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="font-serif text-lg text-clay" aria-hidden="true">
              {String(index + 1).padStart(2, "0")}
            </p>
            <h2 className="mt-3 max-w-md font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-[2.1rem]">
              {section.title}
            </h2>
            <p className="mt-4 max-w-md text-[1.02rem] leading-[1.7] text-charcoal/80">{section.body}</p>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
              Questions worth sitting with
            </p>
            <ul className="mt-4 space-y-2.5">
              {section.questions.map((q) => (
                <li key={q} className="flex gap-3 leading-[1.6] text-charcoal/80">
                  <span className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-clay" aria-hidden="true" />
                  {q}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
              What we might discuss
            </p>
            <p className="mt-4 leading-[1.65] text-charcoal/80">{section.options}</p>
            <CTAButton to="/contact" variant="outline" className="mt-6" testId={`find-care-cta-${section.id}`}>
              Talk With Doris
            </CTAButton>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function FindCare() {
  usePageMeta({
    title: "Find Care — Explore the Possibilities",
    description:
      "Explore senior care options in Los Angeles with CareCraft: planning ahead, support at home, a new place to call home, and guidance for the next step. Complimentary guidance for families.",
    path: "/find-care",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "Find Care" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>Find care</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines lines={[<>Explore the possibilities.</>]} />
            </h1>
            <Reveal delay={0.35}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                Share what matters to you and your family, and CareCraft will help you explore senior
                care options that feel right for this next chapter.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite pb-20">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          {FIND_CARE_SECTIONS.map((s, i) => (
            <SituationSection key={s.id} section={s} index={i} />
          ))}
          <Reveal>
            <p className="mt-6 max-w-2xl text-[0.85rem] leading-relaxed text-charcoal/55">
              This page offers general guidance about senior care options. It is not medical advice,
              diagnosis, or treatment. CareCraft does not provide medical services.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-forest py-20 text-ivory md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
              <div>
                <h2 className="max-w-xl font-serif text-3xl font-medium leading-tight md:text-4xl">
                  Let's find the right way forward, together.
                </h2>
                <p className="mt-4 max-w-xl text-ivory/80">
                  Share what matters most. CareCraft will listen first, then help you explore the
                  options.
                </p>
              </div>
              <CTAButton to="/contact" variant="ivory" testId="find-care-final-cta">
                Talk With Doris
              </CTAButton>
            </div>
          </Reveal>
          <Link
            to="/care-options"
            data-testid="find-care-link-options"
            className="mt-10 inline-flex items-center gap-2 text-ivory/80 transition-colors hover:text-clay"
          >
            Or explore the care options first <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </>
  );
}
